import { useCallback, useEffect, useRef, useState } from "react";

import api from "@helpers/api";

import { MEMBER_ROLE_LABEL } from "../enums";

const DEFAULT_PER_PAGE = 15;

const INITIAL_FILTERS = {
  roles: [],
  plan_ids: [],
  subscription_statuses: [],
};

export default function useMembersList({ communityId, open }) {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [records, setRecords] = useState(0);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [reloadToken, setReloadToken] = useState(0);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const requestIdRef = useRef(0);

  const fetchMembers = useCallback(
    async ({ search: searchArg, filters: filtersArg, page: pageArg }) => {
      if (!communityId) return;

      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
        const params = {
          page: pageArg,
          per_page: DEFAULT_PER_PAGE,
        };
        if (searchArg && searchArg.trim().length >= 2) {
          params.search = searchArg.trim();
        }
        if (filtersArg.roles.length) params.roles = filtersArg.roles;
        if (filtersArg.plan_ids.length) params.plan_ids = filtersArg.plan_ids;
        if (filtersArg.subscription_statuses.length) {
          params.subscription_statuses = filtersArg.subscription_statuses;
        }

        const { data } = await api.get(
          `/community/${communityId}/members/list`,
          params
        );

        if (requestId !== requestIdRef.current) return;

        const list = Array.isArray(data?.data) ? data.data : [];
        setMembers(list);
        setRecords(data?.total ?? list.length);
        setPages(data?.last_page ?? 1);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load community members", error);
        setMembers([]);
        setRecords(0);
        setPages(0);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [communityId]
  );

  const fetchPlans = useCallback(async () => {
    if (!communityId) return;
    setPlansLoading(true);
    try {
      const { data } = await api.get(`/community/${communityId}/plan`);
      setPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load community plans", error);
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setFilters(INITIAL_FILTERS);
    setPage(1);
    fetchPlans();
  }, [open, communityId, fetchPlans]);

  useEffect(() => {
    if (!open) return;
    fetchMembers({ search, filters, page });
  }, [open, search, filters, page, reloadToken, fetchMembers]);

  const handleSearch = useCallback((value) => {
    setSearch((prev) => {
      if (prev === value) return prev;
      setPage(1);
      return value;
    });
  }, []);

  const toggleFilterValue = useCallback((filterKey, value) => {
    setFilters((prev) => {
      const current = new Set(prev[filterKey]);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [filterKey]: Array.from(current) };
    });
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  }, []);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const changeRole = useCallback(
    (member, newRole) => {
      if (!member?.id || !communityId) return;
      if (member.role === newRole) return;

      const fromLabel = MEMBER_ROLE_LABEL[member.role] || member.role;
      const toLabel = MEMBER_ROLE_LABEL[newRole] || newRole;

      window.openConfirm?.({
        title: "Alterar permissão",
        headline: "Deseja alterar a permissão do membro?",
        subtitle: `Alterar de "${fromLabel}" para "${toLabel}"?`,
        buttons: [
          { text: "Cancelar", variant: "text", onClick: () => {} },
          {
            text: "Alterar permissão",
            variant: "contained",
            color: "primary",
            onClick: async () => {
              try {
                await api.patch(`community/member/${member.id}/permission`, {
                  community_id: communityId,
                  role: newRole,
                });
                window.setSnackbar?.("Permissão alterada com sucesso");
                refresh();
              } catch (error) {
                console.error("Failed to change member role", error);
                window.setSnackbar?.("Erro ao alterar permissão");
              }
            },
          },
        ],
      });
    },
    [communityId, refresh]
  );

  const removeMember = useCallback(
    (member) => {
      if (!member?.id || !communityId) return;

      const displayName = member.username || member.nickname || "este membro";

      window.openConfirm?.({
        title: "Remover membro",
        headline: `Remover ${displayName} da comunidade?`,
        subtitle:
          "Esta ação cancela todas as assinaturas ativas do membro nesta comunidade e remove seu acesso.",
        variant: "danger",
        buttons: [
          { text: "Cancelar", variant: "text", onClick: () => {} },
          {
            text: "Remover membro",
            variant: "contained",
            color: "error",
            onClick: async () => {
              try {
                await api.delete(`community/member/${member.id}`);
                window.setSnackbar?.("Membro removido com sucesso");
                refresh();
              } catch (error) {
                console.error("Failed to remove member", error);
                window.setSnackbar?.("Erro ao remover o membro");
              }
            },
          },
        ],
      });
    },
    [communityId, refresh]
  );

  return {
    loading,
    members,
    page,
    pages,
    records,
    search,
    filters,
    plans,
    plansLoading,
    handleSearch,
    toggleFilterValue,
    handlePageChange,
    clearFilters,
    refresh,
    changeRole,
    removeMember,
  };
}
